import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  MeetingBookingService,
  SalesTeamAgent,
} from 'src/app/private/services/meeting-booking.service';
import { LeadService } from 'src/app/private/services/lead.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TrackingService } from 'src/app/private/services/tracking.service';
import {
  getBookingIdFromMeeting,
  isPartEBookingMeeting,
  meetingTimestampFromDateAndTime,
} from 'src/app/utils/meeting-booking.util';

@Component({
  selector: 'app-add-lead-metting',
  templateUrl: './add-lead-metting.component.html',
  styleUrls: ['./add-lead-metting.component.scss']
})
export class AddLeadMettingComponent implements OnChanges {

  isAdding = false;
  isSubmitted = false;
  loading = false;
  isAdmin = false;
  @Input() editFieldValues: any = {};
  @Input() editData: any = {};
  @Input() type = 'Add';
  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();

  recipients: string[] = [''];
  meetingOrganizers: SalesTeamAgent[] = [];
  selectedOrganizerId: number | null = null;

  constructor(
    private api: LeadService,
    private meetingBookingService: MeetingBookingService,
    private toaster: ToastrService,
    private localstorage: LocalStorageService,
    private tracker: TrackingService
  ) {
    this.isAdmin = this.localstorage.checkFullAccessForTableList(
      this.localstorage.getRole()
    );
  }

  today = '';

  meetingForm = {
    client_id: this.localstorage.getClientId(),
    lead_id: this.editFieldValues.lead_id,
    title: '',
    date: '',
    duration: '',
    time: '',
    description: ''
  };



  trackByIndex(index: number): number {
    return index;
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editData'] && this.type === 'Update' && this.editData) {
      this.populateFormFromEditData();
    }
  }

  ngOnInit() {
    let now = new Date();
    this.today = now.toISOString().split('T')[0]; // formats as "yyyy-MM-dd"

    if (this.type === 'Update' && this.editData) {
      this.populateFormFromEditData();
    }
  }

  private populateFormFromEditData(): void {
    const data = this.editData;
    if (!data) {
      return;
    }

    const duration = this.resolveMeetingDuration(data);

    this.meetingForm.title = data.meeting_title || '';
    this.meetingForm.description = data.meeting_desc || '';
    this.meetingForm.date = this.normalizeDateForInput(data.meeting_date);
    this.meetingForm.duration = duration != null ? String(duration) : '';
    this.meetingForm.time = this.normalizeMeetingTimeForInput(data.meeting_time, duration);
    this.timeSlots = this.meetingForm.time ? [this.meetingForm.time] : [];
    this.recipients = this.normalizeRecipients(data.recipients);
  }

  private normalizeDateForInput(dateStr: unknown): string {
    if (dateStr == null || dateStr === '') {
      return '';
    }

    const value = String(dateStr).trim();

    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
      const [day, month, year] = value.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(value)) {
      const [day, month, year] = value.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }

    return '';
  }

  private resolveMeetingDuration(data: any): number | null {
    const direct = Number(data?.duration_minutes ?? data?.duration);
    if (Number.isFinite(direct) && direct > 0) {
      return direct;
    }

    const time = data?.meeting_time;
    if (time && String(time).includes(' - ')) {
      const mins = Number(this.getTimeDifferenceInMinutes(String(time)));
      return Number.isFinite(mins) && mins > 0 ? mins : null;
    }

    return null;
  }

  private normalizeMeetingTimeForInput(time: unknown, durationMin: number | null): string {
    if (time == null || time === '') {
      return '';
    }

    const value = String(time).trim();
    if (value.includes(' - ')) {
      return value;
    }

    if (/^\d{1,2}:\d{2}$/.test(value) && durationMin) {
      const [hour, minute] = value.split(':').map(Number);
      const startTotal = hour * 60 + minute;
      const endTotal = startTotal + durationMin;
      const endHour = Math.floor(endTotal / 60).toString().padStart(2, '0');
      const endMinute = (endTotal % 60).toString().padStart(2, '0');
      return `${value} - ${endHour}:${endMinute}`;
    }

    return value;
  }

  private normalizeRecipients(raw: unknown): string[] {
    if (raw == null || raw === '') {
      return [''];
    }

    let value: unknown = raw;
    if (typeof value === 'string') {
      const rawString = value.trim();
      if (!rawString) {
        return [''];
      }
      try {
        value = JSON.parse(rawString);
      } catch {
        return [rawString];
      }
    }

    if (Array.isArray(value)) {
      const emails = value
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean);
      return emails.length ? emails : [''];
    }

    return [''];
  }

  timeSlots: any[] = [];

  addRecipient() {
    this.recipients.push('');
  }

  removeRecipient(index: number) {
    this.recipients.splice(index, 1);
  }

  durationList = [
    { label: '15 mins', value: 15 },
    { label: '30 mins', value: 30 },
    { label: '45 mins', value: 45 },
    { label: '1 hour', value: 60 }
  ];

  getTimeDifferenceInMinutes(timeRange: string) {
    if (!timeRange || !timeRange.includes(' - ')) {
      return '';
    }

    const [start, end] = timeRange.split(' - ');
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);

    if (
      [startHour, startMinute, endHour, endMinute].some((n) => Number.isNaN(n))
    ) {
      return '';
    }

    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;

    return `${endTotalMinutes - startTotalMinutes}`;
  }

  handleDateChange(): void {
    this.meetingForm.time = '';
    this.timeSlots = [];
    this.selectedOrganizerId = null;
    this.meetingOrganizers = [];

    if (this.meetingForm.date && this.meetingForm.duration) {
      if (this.type === 'Add' || isPartEBookingMeeting(this.editData)) {
        this.loadMeetingSlots();
      }
    }
  }

  handleDurationChange(_event?: any) {
    this.meetingForm.time = '';
    this.timeSlots = [];
    this.selectedOrganizerId = null;
    this.meetingOrganizers = [];

    if (this.type === 'Add' || isPartEBookingMeeting(this.editData)) {
      this.loadMeetingSlots();
    }
  }

  handleOrganizerChange(organizerId: number | string | null = this.selectedOrganizerId): void {
    this.meetingForm.time = '';
    if (organizerId == null || organizerId === '') {
      this.timeSlots = [];
      return;
    }

    const agent = this.findOrganizerById(organizerId);
    this.timeSlots = this.formatAgentSlots(agent?.free_slots || []);
  }

  private findOrganizerById(organizerId: number | string): SalesTeamAgent | undefined {
    const id = String(organizerId);
    return this.meetingOrganizers.find((item) => String(item.user_id) === id);
  }

  private normalizeSalesTeamAgents(res: any): SalesTeamAgent[] {
    const agents = Array.isArray(res) ? res : (res?.agents || []);
    return agents.map((agent: SalesTeamAgent) => ({
      ...agent,
      user_id: Number(agent.user_id),
      free_slots: agent.free_slots || [],
    }));
  }

  private readonly emptySlotsMessage =
    'Please connect your Google or Outlook email account to schedule meetings.';

  private loadMeetingSlots(): void {
    if (!this.meetingForm.date || !this.meetingForm.duration) {
      return;
    }

    this.loading = true;

    const slotRequest: {
      client_id: string | number;
      date: string;
      duration_minutes: number;
      timezone: string;
      user_id?: number | string;
    } = {
      client_id: this.localstorage.getClientId(),
      date: this.meetingForm.date,
      duration_minutes: Number(this.meetingForm.duration),
      timezone: 'Asia/Kolkata',
    };

    if (!this.isAdmin) {
      slotRequest.user_id = this.localstorage.getUserId();
    } else if (this.type === 'Update' && this.editData?.user_id != null) {
      slotRequest.user_id = this.editData.user_id;
    }

    this.meetingBookingService.getSalesTeamWithSlots(slotRequest).subscribe({
      next: (res) => {
        const agents = this.normalizeSalesTeamAgents(res);
        this.loading = false;

        if (!agents.length) {
          this.toaster.warning(res?.message || this.emptySlotsMessage);
          if (this.isAdmin) {
            this.meetingOrganizers = [];
          }
          return;
        }

        if (this.isAdmin) {
          this.meetingOrganizers = agents;

          if (this.type === 'Update') {
            const organizerId =
              this.editData?.user_id != null
                ? Number(this.editData.user_id)
                : agents[0].user_id;
            const agent =
              agents.find((item) => item.user_id === organizerId) || agents[0];
            this.selectedOrganizerId = agent.user_id;
            this.timeSlots = this.formatAgentSlots(agent.free_slots || []);
          }
          return;
        }

        const selfId = Number(this.localstorage.getUserId());
        const selfAgent =
          agents.find((agent) => agent.user_id === selfId) || agents[0];

        this.selectedOrganizerId = selfAgent.user_id;
        this.timeSlots = this.formatAgentSlots(selfAgent.free_slots || []);
      },
      error: (err) => {
        this.toaster.error(err?.error?.message || 'Could not load meeting slots');
        this.loading = false;
      },
    });
  }

  private formatAgentSlots(slots: { start: string; end: string }[]): string[] {
    return (slots || []).map((slot) => {
      const start = this.formatSlotTime(slot.start);
      const end = this.formatSlotTime(slot.end);
      return `${start} - ${end}`;
    });
  }

  private formatSlotTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    });
  }

  private getMeetingIdFromEditData(): string {
    const details = this.editData?.google_meet_details;
    if (!details) {
      return this.editData?.meeting_id || '';
    }
    if (typeof details === 'string') {
      try {
        return JSON.parse(details)?.meeting_id || '';
      } catch {
        return '';
      }
    }
    return details?.meeting_id || '';
  }

  convertToISO(dateStr: any) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  convertToDate(dateStr: any) {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  }

  private getOrganizerUserId(): number | string {
    if (this.type === 'Add' && this.isAdmin && this.selectedOrganizerId != null) {
      return this.selectedOrganizerId;
    }
    return this.localstorage.getUserId();
  }

  private getLeadClientName(): string {
    return (
      this.editFieldValues?.contact_name ||
      [this.editFieldValues?.fields?.first_name, this.editFieldValues?.fields?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim() ||
      ''
    );
  }

  private getLeadClientEmail(): string {
    return (
      this.editFieldValues?.contact_email ||
      this.editFieldValues?.fields?.email ||
      ''
    );
  }

  private getLeadClientPhone(): string {
    return (
      this.editFieldValues?.contact_phone ||
      this.editFieldValues?.fields?.phone ||
      this.editFieldValues?.fields?.mobile ||
      ''
    );
  }

  private getSelectedSlotTimestamp(): number | null {
    const agent = this.findOrganizerById(this.selectedOrganizerId!);
    if (agent?.free_slots?.length && this.meetingForm.time) {
      const matchingSlot = agent.free_slots.find((slot) => {
        const label = this.formatAgentSlots([slot])[0];
        return label === this.meetingForm.time;
      });
      if (matchingSlot?.start) {
        const ts = new Date(matchingSlot.start).getTime();
        if (!Number.isNaN(ts)) {
          return ts;
        }
      }
    }

    return meetingTimestampFromDateAndTime(
      this.meetingForm.date,
      this.meetingForm.time
    );
  }

  private rescheduleMeeting(description: string): void {
    const bookingId = getBookingIdFromMeeting(this.editData);
    if (!bookingId) {
      this.isAdding = false;
      this.legacyUpdateMeeting(description);
      return;
    }

    const meetingTimeStamp = this.getSelectedSlotTimestamp();
    if (!meetingTimeStamp) {
      this.isAdding = false;
      this.toaster.error('Could not resolve the selected meeting time. Please pick the slot again.');
      return;
    }

    this.meetingBookingService.rescheduleMeetingSlot({
      booking_id: bookingId,
      meeting_time_stamp: meetingTimeStamp,
      duration: Number(this.meetingForm.duration),
      timezone: 'Asia/Kolkata',
      meeting_title: this.meetingForm.title,
      meeting_desc: this.meetingForm.description,
      recipients: this.recipients.filter((email) => email?.trim()),
    }).subscribe({
      next: () => {
        this.isAdding = false;
        this.toaster.success('Meeting updated successfully');
        this.tracker.track('Update', 'Lead Meeting', description);
        this.isSubmitted = false;
        this.reload.emit();
        this.handleClosDialog();
      },
      error: (err) => {
        this.isAdding = false;
        this.toaster.error(err?.error?.message || 'Something went wrong');
      },
    });
  }

  private bookMeeting(description: string): void {
    const meetingTimeStamp = this.getSelectedSlotTimestamp();
    if (!meetingTimeStamp) {
      this.isAdding = false;
      this.toaster.error('Could not resolve the selected meeting time. Please pick the slot again.');
      return;
    }

    this.meetingBookingService.bookMeetingSlot({
      client_id: this.localstorage.getClientId(),
      lead_id: this.editFieldValues.lead_id,
      meeting_time_stamp: meetingTimeStamp,
      duration: Number(this.meetingForm.duration),
      timezone: 'Asia/Kolkata',
      client_name: this.getLeadClientName(),
      client_email: this.getLeadClientEmail(),
      client_phone: this.getLeadClientPhone(),
      meeting_title: this.meetingForm.title,
      meeting_desc: this.meetingForm.description,
      booking_source: 'crm',
      assigned_user_id: this.getOrganizerUserId(),
    }).subscribe({
      next: () => {
        this.isAdding = false;
        this.toaster.success('Meeting created successfully');
        this.tracker.track('Create', 'Lead Meeting', description);
        this.isSubmitted = false;
        this.reload.emit();
        this.handleClosDialog();
      },
      error: (err) => {
        this.isAdding = false;
        this.toaster.error(err?.error?.message || 'Something went wrong');
      },
    });
  }

  private legacyUpdateMeeting(description: string): void {
    const paylaod: any = {
      startDate: this.convertToISO(this.meetingForm.date),
      inviteeEmails: this.recipients.filter((email) => email?.trim()),
      timeZone: 'Asia/kolkata',
      duration: this.meetingForm.duration,
      meetingTime: this.meetingForm.time,
      meeting_title: this.meetingForm.title,
      meeting_desc: this.meetingForm.description,
      client_id: this.localstorage.getClientId(),
      user_id: this.editData.user_id || this.localstorage.getUserId(),
      center_id: this.localstorage.getCenterId(),
      lead_id: this.editFieldValues.lead_id,
      meeting_id: this.getMeetingIdFromEditData(),
      id: this.editData.id,
    };

    this.api.updateAppointment(paylaod).subscribe({
      next: () => {
        this.isAdding = false;
        this.toaster.success('Meeting updated successfully');
        this.tracker.track('Update', 'Lead Meeting', description);
        this.isSubmitted = false;
        this.reload.emit();
        this.handleClosDialog();
      },
      error: (err) => {
        this.isAdding = false;
        this.toaster.error(err?.error?.message || 'Something went wrong');
      },
    });
  }

  handleSaveTask() {
    this.isSubmitted = true;
    if (!this.meetingForm.title || !this.meetingForm.date) return;

    if (this.isAdmin && this.type === 'Add' && !this.selectedOrganizerId) {
      this.toaster.error('Please select a meeting organizer');
      return;
    }

    if (!this.meetingForm.duration || !this.meetingForm.time) return;

    this.isAdding = true;

    const invitees =
      this.type === 'Update'
        ? this.recipients.filter((email) => email?.trim()).join(', ')
        : [this.editFieldValues.contact_email, this.localstorage.getEmail()]
            .filter(Boolean)
            .join(', ');

    const description = `Meeting ${this.type === 'Add' ? 'created' : 'updated'} - 
Title: ${this.meetingForm.title}, 
Lead ID: ${this.editFieldValues.lead_id}, 
Date: ${this.meetingForm.date}, 
Time: ${this.meetingForm.time}, 
Duration: ${this.meetingForm.duration} mins, 
Invitees: ${invitees}`;

    if (this.type === 'Add') {
      this.bookMeeting(description);
      return;
    }

    this.rescheduleMeeting(description);
  }

  handleClosDialog() {
    this.close.emit();
  }
}
